import { ConvexError } from 'convex/values'

import type { MutationCtx, QueryCtx } from '../_generated/server'
import { TTS_MONTHLY_LIMIT, VOICE_CALL_MONTHLY_LIMIT_MIN } from './coach'
import { DEFAULT_DAY_START_HOUR, logicalDayStr } from './day'
import { DOT_IMAGE_DAILY_LIMIT } from './habitDots'
import { LIFETIME_AI_SHARE, withEffectivePlan } from './plan'

export type MeteredPlan = 'trial' | 'pro'
export type UsagePlan = 'anonymous' | 'free' | MeteredPlan
type Period = 'trial' | 'month' | 'day' | 'hour'
type Quota = { limit: number; period: Period }

// Every AI cap in one place. 'trial' periods are lifetime trial totals; 'month'
// is the UTC month; 'hour' the UTC hour; 'day' is the user's logical local day.
// Free users are gated by the Pro checks except where a `free` quota is set.
export const USAGE_LIMITS = {
  coachChat: { trial: { limit: 20, period: 'day' }, pro: { limit: 100, period: 'day' } },
  brainDump: { trial: { limit: 15, period: 'trial' }, pro: { limit: 200, period: 'month' } },
  coachMe: { trial: { limit: 2, period: 'trial' }, pro: { limit: 50, period: 'month' } },
  tts: {
    trial: { limit: 15, period: 'trial' },
    pro: { limit: TTS_MONTHLY_LIMIT, period: 'month' },
  },
  // Seconds.
  voiceCall: {
    trial: { limit: 10 * 60, period: 'trial' },
    pro: { limit: VOICE_CALL_MONTHLY_LIMIT_MIN * 60, period: 'month' },
  },
  // Full plan builds/rewrites. Signed-out visitors are keyed by a client-made id,
  // which is spoofable, so `anonymousGlobal` also caps all of them together.
  planGeneration: {
    anonymous: { limit: 3, period: 'day' },
    anonymousGlobal: { limit: 500, period: 'hour' },
    free: { limit: 8, period: 'day' },
    trial: { limit: 15, period: 'day' },
    pro: { limit: 60, period: 'day' },
  },
  // AI images for custom habit dots.
  habitDotImage: {
    trial: { limit: 5, period: 'day' },
    pro: { limit: DOT_IMAGE_DAILY_LIMIT, period: 'day' },
  },
  // Small helpers around planning (goal ideas, task splits).
  planAssist: {
    anonymous: { limit: 10, period: 'day' },
    anonymousGlobal: { limit: 1500, period: 'hour' },
    free: { limit: 30, period: 'day' },
    trial: { limit: 60, period: 'day' },
    pro: { limit: 200, period: 'day' },
  },
} as const satisfies Record<
  string,
  Record<MeteredPlan, Quota> & Partial<Record<'anonymous' | 'anonymousGlobal' | 'free', Quota>>
>

// Active (non-inbox, not done) goals allowed on Free.
export const FREE_GOAL_LIMIT = 3
// Active (not archived) habits allowed on Free; existing ones over the cap are kept.
export const FREE_HABIT_LIMIT = 3

export type UsageFeature = keyof typeof USAGE_LIMITS
export type PlanFeature = 'planGeneration' | 'planAssist'
export const PLAN_FEATURES: PlanFeature[] = ['planGeneration', 'planAssist']
// Trial/Pro features reported by getMyUsage (TTS + voice calls keep their own tables).
export const COUNTER_FEATURES = ['coachChat', 'brainDump', 'coachMe'] as const
export type CounterFeature = (typeof COUNTER_FEATURES)[number] | PlanFeature | 'habitDotImage'

export type UsageLimitError =
  | { kind: 'usageLimit'; feature: UsageFeature; plan: UsagePlan; limit: number }
  | { kind: 'usageLimit'; feature: 'habits'; plan: 'free'; limit: number }

export function usageLimitError(feature: UsageFeature, plan: UsagePlan, limit: number) {
  return new ConvexError<UsageLimitError>({ kind: 'usageLimit', feature, plan, limit })
}

export function habitLimitError() {
  return new ConvexError<UsageLimitError>({
    kind: 'usageLimit',
    feature: 'habits',
    plan: 'free',
    limit: FREE_HABIT_LIMIT,
  })
}

export function periodKey(
  period: Period,
  now: number,
  tzOffsetMin = 0,
  dayStartHour = DEFAULT_DAY_START_HOUR,
): string {
  if (period === 'trial') return 'trial'
  if (period === 'month') return new Date(now).toISOString().slice(0, 7)
  if (period === 'hour') return new Date(now).toISOString().slice(0, 13)
  return logicalDayStr(now, tzOffsetMin, dayStartHour)
}

export type ResolvedQuota = { plan: MeteredPlan; limit: number; periodKey: string }

type QuotaOpts = { now?: number; tzOffsetMin?: number }

async function tierAndPrefs(ctx: QueryCtx, userId: string, now: number) {
  const [sub, prefs] = await Promise.all([
    ctx.db
      .query('userSubscriptions')
      .withIndex('by_user', (q) => q.eq('userId', userId))
      .first(),
    ctx.db
      .query('userPreferences')
      .withIndex('by_user', (q) => q.eq('userId', userId))
      .first(),
  ])
  const plan = withEffectivePlan(sub, prefs?.proTrialEndsAt, now)
  return { tier: plan.tier, share: plan.lifetime ? LIFETIME_AI_SHARE : 1, prefs }
}

function userPeriodKey(
  q: Quota,
  now: number,
  opts: QuotaOpts,
  prefs: { tzOffsetMinutes?: number; dayStartHour?: number } | null,
) {
  return periodKey(
    q.period,
    now,
    opts.tzOffsetMin ?? prefs?.tzOffsetMinutes ?? 0,
    prefs?.dayStartHour ?? DEFAULT_DAY_START_HOUR,
  )
}

// The caller's current cap for a feature, or null on the free plan.
export async function meteredQuota(
  ctx: QueryCtx,
  userId: string,
  feature: UsageFeature,
  opts: QuotaOpts = {},
): Promise<ResolvedQuota | null> {
  const now = opts.now ?? Date.now()
  const { tier, share, prefs } = await tierAndPrefs(ctx, userId, now)
  if (tier === 'free') return null
  const q: Quota = USAGE_LIMITS[feature][tier]
  return {
    plan: tier,
    limit: Math.ceil(q.limit * share),
    periodKey: userPeriodKey(q, now, opts, prefs),
  }
}

// Plan features are metered on every tier, free included.
export async function planQuota(
  ctx: QueryCtx,
  userId: string,
  feature: PlanFeature,
  opts: QuotaOpts = {},
) {
  const now = opts.now ?? Date.now()
  const { tier, share, prefs } = await tierAndPrefs(ctx, userId, now)
  const q: Quota = USAGE_LIMITS[feature][tier]
  return {
    plan: tier,
    limit: Math.ceil(q.limit * share),
    periodKey: userPeriodKey(q, now, opts, prefs),
  }
}

function counterRow(ctx: QueryCtx, userId: string, feature: CounterFeature, key: string) {
  return ctx.db
    .query('usageCounters')
    .withIndex('by_user_and_feature_and_periodKey', (q) =>
      q.eq('userId', userId).eq('feature', feature).eq('periodKey', key),
    )
    .unique()
}

export async function getUsed(
  ctx: QueryCtx,
  userId: string,
  feature: CounterFeature,
  key: string,
): Promise<number> {
  return (await counterRow(ctx, userId, feature, key))?.count ?? 0
}

export async function getRemaining(
  ctx: QueryCtx,
  userId: string,
  feature: CounterFeature,
  { limit, periodKey: key }: { limit: number; periodKey: string },
): Promise<number> {
  return Math.max(0, limit - (await getUsed(ctx, userId, feature, key)))
}

// Atomically reserve `amount` uses; throws a typed usageLimit error when it would
// exceed the cap. Returns what's left afterwards.
export async function checkAndConsume(
  ctx: MutationCtx,
  userId: string,
  feature: CounterFeature,
  {
    limit,
    periodKey: key,
    plan,
    amount = 1,
  }: { limit: number; periodKey: string; plan: UsagePlan; amount?: number },
): Promise<number> {
  const row = await counterRow(ctx, userId, feature, key)
  const used = row?.count ?? 0
  if (used + amount > limit) throw usageLimitError(feature, plan, limit)
  if (row) await ctx.db.patch(row._id, { count: used + amount })
  else await ctx.db.insert('usageCounters', { userId, feature, periodKey: key, count: amount })
  return limit - used - amount
}

export async function refundUsage(
  ctx: MutationCtx,
  userId: string,
  feature: CounterFeature,
  key: string,
  amount = 1,
): Promise<void> {
  const row = await counterRow(ctx, userId, feature, key)
  if (row && row.count > 0) await ctx.db.patch(row._id, { count: Math.max(0, row.count - amount) })
}

// For work that runs later (replans): refuse up front once the cap is spent,
// then count with addPlanUsage only when it succeeds.
export async function assertPlanUsageLeft(ctx: QueryCtx, userId: string, feature: PlanFeature) {
  const quota = await planQuota(ctx, userId, feature)
  if ((await getRemaining(ctx, userId, feature, quota)) <= 0)
    throw usageLimitError(feature, quota.plan, quota.limit)
}

export async function addPlanUsage(ctx: MutationCtx, userId: string, feature: PlanFeature) {
  const { periodKey: key } = await planQuota(ctx, userId, feature)
  const row = await counterRow(ctx, userId, feature, key)
  if (row) await ctx.db.patch(row._id, { count: row.count + 1 })
  else await ctx.db.insert('usageCounters', { userId, feature, periodKey: key, count: 1 })
}

export const ANON_GLOBAL_ID = 'anon:*'
const ANON_ID = /^[A-Za-z0-9_-]{8,64}$/

// The counter rows a plan reservation took, to hand back to refundPlanUsage.
export type PlanReservation = Array<{ userId: string; periodKey: string }>

export async function reservePlanUsage(
  ctx: MutationCtx,
  feature: PlanFeature,
  who: { userId?: string; anonId?: string },
  opts: QuotaOpts = {},
): Promise<PlanReservation> {
  if (who.userId) {
    const quota = await planQuota(ctx, who.userId, feature, opts)
    await checkAndConsume(ctx, who.userId, feature, quota)
    return [{ userId: who.userId, periodKey: quota.periodKey }]
  }
  const now = opts.now ?? Date.now()
  const { anonymous, anonymousGlobal } = USAGE_LIMITS[feature]
  const globalKey = periodKey(anonymousGlobal.period, now)
  await checkAndConsume(ctx, ANON_GLOBAL_ID, feature, {
    limit: anonymousGlobal.limit,
    periodKey: globalKey,
    plan: 'anonymous',
  })
  const rows = [{ userId: ANON_GLOBAL_ID, periodKey: globalKey }]
  // Clients that predate visitor ids only get the global cap.
  if (!who.anonId || !ANON_ID.test(who.anonId)) return rows
  // Clamped to real offsets so a spoofed tz can't mint extra days.
  const tz = Math.max(-840, Math.min(720, opts.tzOffsetMin ?? 0))
  const userId = `anon:${who.anonId}`
  const key = periodKey(anonymous.period, now, tz)
  await checkAndConsume(ctx, userId, feature, {
    limit: anonymous.limit,
    periodKey: key,
    plan: 'anonymous',
  })
  return [...rows, { userId, periodKey: key }]
}

export async function refundPlanUsage(
  ctx: MutationCtx,
  feature: PlanFeature,
  rows: PlanReservation,
): Promise<void> {
  for (const r of rows) await refundUsage(ctx, r.userId, feature, r.periodKey)
}
